<template>
  <div :class="componentClasses">
    <header class="stk-action-callout-head">
      <Icon
        v-if="icon"
        :icon="icon"
      />

      <p class="stk-action-callout-title">
        {{ title }}
      </p>

      <Badge
        v-if="badge"
        :status="status"
        outlined
      >
        {{ badge }}
      </Badge>
    </header>

    <div class="stk-action-callout-body">
      <slot />
    </div>

    <div
      v-if="action"
      class="stk-action-callout-action"
    >
      Action: <strong>{{ action }}</strong>
    </div>

    <div
      v-if="ctaLabel"
      class="stk-action-callout-foot"
    >
      <Button
        variant="ghost"
        size="3"
        spacing="none"
        :icon-after="ctaIcon"
        @click="emit('click', count)"
      >
        {{ ctaLabel }}
      </Button>
    </div>
  </div>
</template>

<script setup>
import { computed, defineEmits } from 'vue';
import Badge from '@stakeui/content/badge/Badge.vue';
import Button from '@stakeui/form/button/Button.vue';
import Icon from '@stakeui/graphic/icon/Icon.vue';

const props = defineProps({
  status: String,
  icon: String,
  action: String,
  title: String,
  badge: String,
  size: String,
  ctaLabel: String,
  ctaIcon: String,
});

const emit = defineEmits(['click']);

const componentClasses = computed(() => [
  'stk-action-callout',
  { [`is-${props.status}`]: props.status },
  props.size ? `stk-size-${props.size}` : '',
]);
</script>

<style lang="css">
.stk-action-callout {
  --background-color: var(--stk-text-95);
  --color: var(--stk-text-30);
  background-color: var(--background-color);
  color: var(--color);
  display: flex;
  flex-direction: column;
  line-height: 1.25;
  padding: 0.75em 1em;
  border-radius: 0.75em;
  gap: 0.5em;
  text-align: start;

  &.is-primary {
    --background-color: var(--stk-primary-90);
    --color: var(--stk-primary-30);
  }

  &.is-success {
    --background-color: var(--stk-success-90);
    --color: var(--stk-success-30);
  }

  &.is-info {
    --background-color: var(--stk-info-95);
    --color: var(--stk-info-30);
  }

  &.is-warning {
    --background-color: var(--stk-warning-90);
    --color: var(--stk-warning-30);
  }

  &.is-danger {
    --background-color: var(--stk-danger-90);
    --color: var(--stk-danger-30);
  }
}

.stk-action-callout-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5em;
}

.stk-action-callout-title {
  flex-grow: 1;
  font-weight: 500;
}

.stk-action-callout-body {
  font-size: 0.875em;
}

.stk-action-callout-action {
  background-color: rgb(255 255 255 / 80%);
  font-size: 0.875em;
  border-radius: 0.5em;
  padding: 0.5em 0.75em;
  color: var(--stk-text-normal);

  strong {
    color: var(--stk-text-strong);
    font-weight: 600;
  }
}

.stk-action-callout-foot {
  display: flex;
  justify-content: end;
}
</style>
